import { supabase } from '@/lib/supabase'
import type {
  QmsAction,
  QmsActionInsert,
  QmsActionUpdate,
  QmsActionWithOwner,
  QmsActionStats,
  ActionStatus,
  ActionSource,
} from '@/types/qms'
import { profileService } from './profileService'

export const qmsActionService = {
  // ============================================
  // ACTIONS CRUD
  // ============================================

  /**
   * Get all QMS actions for the current organization
   */
  async getActions(): Promise<QmsAction[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching QMS actions:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get actions with owner details
   */
  async getActionsWithOwners(): Promise<QmsActionWithOwner[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('qms_actions')
      .select(`
        *,
        owner:profiles!qms_actions_owner_id_fkey(
          id,
          full_name
        )
      `)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching QMS actions with owners:', error)
      return []
    }

    return (data ?? []) as QmsActionWithOwner[]
  },

  /**
   * Get a single action by ID
   */
  async getAction(id: string): Promise<QmsAction | null> {
    if (!id?.trim()) return null

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching QMS action:', error)
      return null
    }

    return data
  },

  /**
   * Get a single action with owner details
   */
  async getActionWithOwner(id: string): Promise<QmsActionWithOwner | null> {
    if (!id?.trim()) return null

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('qms_actions')
      .select(`
        *,
        owner:profiles!qms_actions_owner_id_fkey(
          id,
          full_name
        )
      `)
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching QMS action with owner:', error)
      return null
    }

    return data as QmsActionWithOwner
  },

  /**
   * Create a new action
   */
  async createAction(action: QmsActionInsert): Promise<QmsAction | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('qms_actions')
      .insert({
        ...action,
        organization_id: orgId,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating QMS action:', error)
      throw error
    }

    return data
  },

  /**
   * Update an action
   */
  async updateAction(id: string, updates: QmsActionUpdate): Promise<QmsAction | null> {
    if (!id?.trim()) throw new Error('Invalid action ID')

    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data, error } = await supabase
      .from('qms_actions')
      .update(updates)
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single()

    if (error) {
      console.error('Error updating QMS action:', error)
      throw error
    }

    return data
  },

  /**
   * Delete an action
   */
  async deleteAction(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid action ID')

    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { error } = await supabase
      .from('qms_actions')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error deleting QMS action:', error)
      throw error
    }

    return true
  },

  // ============================================
  // FILTERED QUERIES
  // ============================================

  /**
   * Get actions by status
   */
  async getActionsByStatus(status: ActionStatus): Promise<QmsAction[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('organization_id', orgId)
      .eq('status', status)
      .order('due_date', { ascending: true })

    if (error) {
      console.error('Error fetching actions by status:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get actions by source
   */
  async getActionsBySource(source: ActionSource): Promise<QmsAction[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('organization_id', orgId)
      .eq('source', source)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching actions by source:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get open/active actions (not completed)
   */
  async getOpenActions(): Promise<QmsAction[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('organization_id', orgId)
      .not('status', 'eq', 'completed')
      .order('due_date', { ascending: true })

    if (error) {
      console.error('Error fetching open actions:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get overdue actions
   */
  async getOverdueActions(): Promise<QmsAction[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const today = new Date().toISOString().split('T')[0]

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('organization_id', orgId)
      .not('status', 'in', '("completed","pending_verification")')
      .lt('due_date', today)
      .order('due_date', { ascending: true })

    if (error) {
      console.error('Error fetching overdue actions:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get actions due within a number of days
   */
  async getActionsDueSoon(daysAhead = 7): Promise<QmsAction[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const today = new Date()
    const futureDate = new Date()
    futureDate.setDate(today.getDate() + daysAhead)

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('organization_id', orgId)
      .not('status', 'in', '("completed","pending_verification")')
      .gte('due_date', today.toISOString().split('T')[0])
      .lte('due_date', futureDate.toISOString().split('T')[0])
      .order('due_date', { ascending: true })

    if (error) {
      console.error('Error fetching actions due soon:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get actions assigned to a specific user
   */
  async getActionsByOwner(ownerId: string): Promise<QmsAction[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('qms_actions')
      .select('*')
      .eq('organization_id', orgId)
      .eq('owner_id', ownerId)
      .order('due_date', { ascending: true })

    if (error) {
      console.error('Error fetching actions by owner:', error)
      return []
    }

    return data ?? []
  },

  // ============================================
  // STATISTICS
  // ============================================

  /**
   * Get action statistics for dashboard
   */
  async getActionStats(): Promise<QmsActionStats> {
    const actions = await this.getActions()

    const now = new Date()
    const todayStr = now.toISOString().split('T')[0]
    const today = new Date(todayStr)
    const weekFromNow = new Date(today)
    weekFromNow.setDate(today.getDate() + 7)
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

    const byStatus = actions.reduce((acc, action) => {
      acc[action.status] = (acc[action.status] || 0) + 1
      return acc
    }, {} as Partial<Record<ActionStatus, number>>)

    const bySource = actions.reduce((acc, action) => {
      acc[action.source] = (acc[action.source] || 0) + 1
      return acc
    }, {} as Partial<Record<ActionSource, number>>)

    const overdue = actions.filter((a) => {
      if (!a.due_date || a.status === 'completed') return false
      return a.due_date < todayStr
    }).length

    const weekFromNowStr = weekFromNow.toISOString().split('T')[0]
    const dueThisWeek = actions.filter((a) => {
      if (!a.due_date || a.status === 'completed') return false
      return a.due_date >= todayStr && a.due_date <= weekFromNowStr
    }).length

    const completedThisMonth = actions.filter((a) => {
      if (!a.completed_at) return false
      return new Date(a.completed_at) >= startOfMonth
    }).length

    return {
      total: actions.length,
      byStatus,
      bySource,
      overdue,
      dueThisWeek,
      completedThisMonth,
    }
  },

  // ============================================
  // STATUS TRANSITIONS
  // ============================================

  /**
   * Mark action as in progress
   */
  async startAction(id: string): Promise<QmsAction | null> {
    return this.updateAction(id, { status: 'in_progress' })
  },

  /**
   * Mark action as ready for verification
   */
  async completeAction(id: string, completionNotes?: string): Promise<QmsAction | null> {
    return this.updateAction(id, {
      status: 'pending_verification',
      completed_at: new Date().toISOString(),
      completion_notes: completionNotes,
    })
  },

  /**
   * Verify and close an action
   */
  async verifyAction(id: string, verificationNotes?: string): Promise<QmsAction | null> {
    const { data: { user } } = await supabase.auth.getUser()

    return this.updateAction(id, {
      status: 'completed',
      verified_at: new Date().toISOString(),
      verified_by: user?.id,
      verification_notes: verificationNotes,
    })
  },

  /**
   * Reopen an action
   */
  async reopenAction(id: string): Promise<QmsAction | null> {
    return this.updateAction(id, {
      status: 'open',
      completed_at: null,
      verified_at: null,
      verified_by: null,
      completion_notes: null,
      verification_notes: null,
    })
  },
}
